import { useEffect, useRef, useState, type Ref } from 'react';
import { Check, Pause, Play, RotateCcw, Sparkles, Terminal } from 'lucide-react';
import { cn } from '@/shared/cn';
import { Button } from '@/design-system/Button';
import { GlassCard } from '@/design-system/GlassCard';
import { Pill } from '@/design-system/Pill';
import { SurfacePageSectionHeader } from '@/design-system/SurfacePage';
import type { AgentTrace } from '@/shared/contracts/analytics/v1';
import {
  agentDecisionLabel,
  agentToolLabel,
  approvedPillText,
  formatAgentDuration,
  gateSeatLabel,
  formatAgentEstimate,
  estimateBasisLine,
  type ActionGateRead,
  type AgentApprovalRecord,
  type AgentDecisionKind,
  type AgentInvestigationRow,
  type AgentRecommendation,
} from '@/versions/v1/modules/advisory/AgentInvestigation.transform';
import {
  EXCURSION_WORKFLOW_NAME,
  investigationRunStatusLabel,
  type InvestigationRun,
  type InvestigationRunStatus,
} from '@/versions/v1/modules/advisory/InvestigationRun.transform';
import { RUN_ESTIMATE_LABEL } from '@/versions/v1/modules/advisory/AnalysisRun.transform';
import { GATE_APPROVED_SURFACE, GATE_GHOST_BUTTON, GATE_PRIMARY_BUTTON, gatePillClass } from './gate-ui';
import { ActionGateControls } from './ActionGateControls';

// K7 r4: the band keeps the run-estimate name whole so `Run / estimate` never
// splits across two lines; the qualifier after it may still wrap. The rendered
// text is unchanged — only the nowrap boundary moves.
const RUN_ESTIMATE_NAME = RUN_ESTIMATE_LABEL.slice(0, RUN_ESTIMATE_LABEL.indexOf(' · '));
const RUN_ESTIMATE_REST = RUN_ESTIMATE_LABEL.slice(RUN_ESTIMATE_NAME.length);

export type InvestigationReplayViewProps = {
  trace: AgentTrace;
  rows: AgentInvestigationRow[];
  visibleCount: number;
  recommendation: AgentRecommendation | null;
  decision: AgentApprovalRecord | null;
  decisionAtLabel?: string;
  decisionReason: string;
  decisionEnabled: boolean;
  canDecide: boolean;
  playing: boolean;
  cursorLabel: string;
  onPlay: () => void;
  onDecisionReasonChange: (value: string) => void;
  onDecision: (kind: AgentDecisionKind) => void;
  compact?: boolean;
  showRecommendation?: boolean;
  controlPlanLabel?: string;
  /** Case page only: run time and duration printed beside the exchange count. */
  runLabel?: string;
  /** Case page only (K13 / A4): proposition, figure line and recovery line of the action gate. */
  actionGate?: ActionGateRead;
  /** Case page only: the approving seat named on the primary action (`Approve · Control metallurgist`). */
  approverLabel?: string;
  /** Case page only: the session's action approval (D19), so the gate stays closed across a remount. */
  actionApproval?: { at: string; by: string };
  /**
   * K15 / D24: the case's Investigation runs. The column plays the last run the
   * replay clock has passed; while the case has not reached Diagnosis it shows
   * no run affordance and no trace, the way any unreached stage reads.
   */
  run?: InvestigationRun | null;
  runStatus?: InvestigationRunStatus;
  /** K15: the workflow's trigger has fired (the case has reached Diagnosis). */
  canRun?: boolean;
  /** K15: start a new run of the Excursion investigation workflow. */
  onRun?: () => void;
  /** K15: pause / resume / replay the run on screen. */
  onTogglePlayback?: () => void;
  /** K15: each row's own case-clock step time, so the trace reads as it lands. */
  stepClocks?: readonly string[];
  /** K15: the recommendation's case-clock time (the run's end). */
  endClock?: string;
  /**
   * #731: false for the floor seats (Operator, Control room), whose estimate
   * band and gate read the non-money basis, never a US$ figure.
   */
  showMoney?: boolean;
};

/** The recorded sequence view, without the fetcher or page-state decision owner. */
export function InvestigationReplayView(props: InvestigationReplayViewProps) {
  if (props.compact) return <CaseInvestigationView {...props} />;
  return DrawerInvestigationView(props);
}

/**
 * K15: the workflow the column is a run of. Before a run it names the workflow
 * only — no trigger copy and no caveat, the way the case page reads a stage it
 * has not reached (K15 round 2). Once a run exists it carries the run id, its
 * case-clock start and its status, so the column never shows a trace without
 * saying whose it is.
 */
export function WorkflowStrip({
  run,
  status,
  className,
}: {
  run?: InvestigationRun | null;
  status?: InvestigationRunStatus;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0', className)} data-investigation-workflow>
      <span className="block truncate text-xs font-semibold text-ink-primary">{EXCURSION_WORKFLOW_NAME}</span>
      {run ? (
        <p className="mt-0.5 flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-[10px] leading-snug text-muted">
          <span className="font-mono text-ink-secondary" data-investigation-run-id>{run.id}</span>
          <span>started {run.startedAt.slice(8, 10)} {formatRunStartMonth(run.startedAt)} {run.startedAt.slice(11, 16)}</span>
          {status ? (
            <span data-investigation-run-status={status} className={cn('uppercase tracking-[0.05em]', status === 'approved' ? 'text-[var(--state-online)]' : undefined)}>
              {investigationRunStatusLabel(status)}
            </span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `08 Jun` from `2026-06-08T09:30` — the strip's own read, without importing a second clock helper. */
function formatRunStartMonth(startedAt: string): string {
  return MONTHS_SHORT[Number(startedAt.slice(5, 7)) - 1] ?? '';
}

/**
 * K15: `Run investigation` is the primary affordance and always starts a new
 * run; the ghost control pauses, resumes or replays the run on screen. The
 * button is offered only once the workflow's trigger has fired (`canRun`), so a
 * case short of Diagnosis shows no run affordance at all (K15 round 2). The
 * step count lives in the column header only, never twice.
 */
export function RunControls({
  run,
  playing,
  complete,
  canRun,
  onRun,
  onTogglePlayback,
  className,
}: {
  run?: InvestigationRun | null;
  playing: boolean;
  complete: boolean;
  canRun?: boolean;
  onRun?: () => void;
  onTogglePlayback?: () => void;
  className?: string;
}) {
  if (!canRun && !run) return null;
  return (
    <div className={cn('flex min-w-0 flex-wrap items-center gap-1.5', className)} data-investigation-run-controls>
      {canRun ? (
        <button
          type="button"
          className={cn(GATE_PRIMARY_BUTTON, 'h-7 gap-1 px-2.5 text-[11px]')}
          onClick={onRun}
          data-run-investigation
        >
          <Sparkles className="size-3" aria-hidden="true" />
          Run investigation
        </button>
      ) : null}
      {run ? (
        <button
          type="button"
          className={cn(GATE_GHOST_BUTTON, 'h-7 gap-1 px-2 text-[11px]')}
          onClick={onTogglePlayback}
          data-run-playback-toggle
          aria-label={playing ? 'Pause investigation run' : complete ? 'Replay investigation run' : 'Resume investigation run'}
        >
          {playing ? <Pause className="size-3" aria-hidden="true" /> : complete ? <RotateCcw className="size-3" aria-hidden="true" /> : <Play className="size-3" aria-hidden="true" />}
          {playing ? 'Pause' : complete ? 'Replay' : 'Resume'}
        </button>
      ) : null}
    </div>
  );
}

/** Drawer layout: full-depth rows and recommendation. Hook-free so probes can call it as a function. */
function DrawerInvestigationView({
  trace,
  rows,
  visibleCount,
  recommendation,
  decision,
  decisionAtLabel,
  decisionReason,
  decisionEnabled,
  canDecide,
  playing,
  cursorLabel,
  onPlay,
  onDecisionReasonChange,
  onDecision,
  run,
  runStatus,
  canRun,
  onRun,
  onTogglePlayback,
  stepClocks,
}: InvestigationReplayViewProps) {
  const visibleRows = rows.slice(0, visibleCount);
  const complete = visibleCount >= rows.length;
  const estimate = recommendation ? formatAgentEstimate(recommendation.estimated_value) : null;

  return (
    <section
      className="space-y-3"
      aria-label="Recorded agent investigation"
      data-trace-model={trace.model}
      data-trace-recorded-at={trace.recorded_at}
    >
      {WorkflowStrip({ run, status: runStatus })}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {RunControls({
          run,
          playing,
          complete,
          canRun: canRun ?? false,
          onRun,
          onTogglePlayback: onTogglePlayback ?? onPlay,
        })}
        {complete ? <Pill tone="success">Recommendation ready</Pill> : null}
      </div>

      {visibleRows.length > 0 ? (
        <ol className="space-y-2" aria-label="Agent tool trace">
          {visibleRows.map((row, index) => (
            <li
              key={`${row.tool}-${index}`}
              data-agent-tool={row.tool}
              className="rounded-lg border border-[var(--hairline)] px-3 py-2.5"
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
                <Terminal size={14} aria-hidden="true" />
                <span className="font-medium text-zinc-900 dark:text-zinc-100">
                  {agentToolLabel(row.tool)}
                </span>
                {row.argumentLabel ? <span>{row.argumentLabel}</span> : null}
                <span className="ml-auto tabular-nums" data-agent-step-clock={stepClocks?.[index] ?? ''}>
                  {stepClocks?.[index] ?? formatAgentDuration(row.elapsedMs)}
                </span>
              </div>
              <p className="mt-1 text-sm font-medium">{row.resultExcerpt}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted">{row.reasoning}</p>
            </li>
          ))}
        </ol>
      ) : null}

      {complete && recommendation && estimate ? (
        <>
          <GlassCard className="space-y-2.5 px-3 py-3 text-sm" data-agent-recommendation>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted">Most likely cause</p>
              <p className="font-medium" data-cause-id={recommendation.cause_id}>{recommendation.cause_label}</p>
            </div>
            <div className="space-y-2 border-t border-[var(--hairline)] pt-2">
              <p className="leading-relaxed">
                <span className="text-xs uppercase tracking-wide text-muted">Control Plan response · {recommendation.control_plan_row_id}</span>
                <br />{recommendation.control_plan_action}
              </p>
              <p className="leading-relaxed">
                <span className="text-xs uppercase tracking-wide text-muted">TARP · Tier {recommendation.tarp_tier}</span>
                <br />{recommendation.tarp_instruction}
              </p>
              <div className="rounded-lg border border-[var(--hairline)] px-3 py-2">
                <p className="text-xs uppercase tracking-wide text-muted">Estimated EBITDA impact · {recommendation.estimated_value.basis}</p>
                <p className="font-medium tabular-nums">{estimate.impact} if recovery gap is closed</p>
                <p className="mt-0.5 text-sm" data-agent-estimate-recovery>{estimate.recovery} · {estimate.shortfall}</p>
              </div>
            </div>
            <div className="border-t border-[var(--hairline)] pt-2">
              <p className="mb-1 text-xs uppercase tracking-wide text-muted">Evidence</p>
              <ul className="space-y-1 text-sm leading-relaxed">
                {recommendation.evidence.map((fact, index) => <li key={index}>{fact}</li>)}
              </ul>
            </div>
            <div className="space-y-1 border-t border-[var(--hairline)] pt-2 text-sm">
              <p><span className="text-muted">Confidence</span>{' · '}{recommendation.confidence_pct}% · cause unconfirmed</p>
              <p className="leading-relaxed"><span className="text-muted">What to check</span>{' · '}{recommendation.human_check}</p>
            </div>
          </GlassCard>

          <div className="space-y-2 border-t border-[var(--hairline)] pt-3" data-approval-gate>
            {!decision ? <Pill tone="warning">Draft — not live until approved</Pill> : null}
            <SurfacePageSectionHeader
              title="Human decision"
              actions={<span className="text-xs text-muted">{cursorLabel}</span>}
            />
            {decision ? (
              DecisionRecord({ decision, decisionAtLabel })
            ) : canDecide ? (
              <div className="space-y-2">
                <textarea
                  aria-label="Decision reason"
                  className="min-h-16 w-full rounded-lg border border-[var(--hairline)] bg-transparent px-3 py-2 text-sm"
                  placeholder="Reason"
                  value={decisionReason}
                  onChange={(event) => onDecisionReasonChange(event.target.value)}
                />
                {DecisionButtons({ decisionEnabled, onDecision })}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}

/** Hook-free, so the drawer view can inline it and stay walkable as a plain function call. */
function DecisionRecord({
  decision,
  decisionAtLabel,
  className = 'rounded-lg border border-[var(--hairline)] px-3 py-2 text-sm',
  recordRef,
}: {
  decision: AgentApprovalRecord;
  decisionAtLabel?: string;
  className?: string;
  /** Case page: the record takes focus after a decision so it does not fall to the body. */
  recordRef?: Ref<HTMLParagraphElement>;
}) {
  return (
    <p ref={recordRef} tabIndex={recordRef ? -1 : undefined} className={className} data-approval-record>
      <Check size={14} className="mr-1 inline text-[var(--state-online)]" aria-hidden="true" />
      {agentDecisionLabel(decision.kind)} · {decision.approver} · {decisionAtLabel ?? decision.at}
      {' · '}{decision.reason}
    </p>
  );
}

function DecisionButtons({
  decisionEnabled,
  onDecision,
  className,
}: {
  decisionEnabled: boolean;
  onDecision: (kind: AgentDecisionKind) => void;
  className?: string;
}) {
  return (
    <div className="flex flex-wrap gap-2" data-agent-decision-controls>
      <Button type="button" variant="primary" className={className} disabled={!decisionEnabled} onClick={() => onDecision('approved')}>
        Approve
      </Button>
      <Button type="button" variant="ghost" className={className} disabled={!decisionEnabled} onClick={() => onDecision('modified')}>
        Modify
      </Button>
      <Button type="button" variant="ghost" className={className} disabled={!decisionEnabled} onClick={() => onDecision('rejected')}>
        Reject
      </Button>
    </div>
  );
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * K15 round 2: scroll the bounded trace to a row boundary so the first row in
 * view is never cut in half under the controls. The last row (and the
 * recommendation marker) stays fully visible by padding the list's bottom by
 * just the amount that puts the chosen boundary within reach. The top fade in
 * the view is the belt to this braces: a cut row never reads as a defect.
 */
function scrollListToRowBoundary(list: HTMLOListElement, smooth: boolean): void {
  const rows = [...list.querySelectorAll<HTMLElement>('[data-agent-tool], [data-agent-trace-end]')];
  const last = rows.at(-1);
  if (!last) return;
  const viewportTop = list.getBoundingClientRect().top;
  const offsetTop = (node: HTMLElement) => node.getBoundingClientRect().top - viewportTop + list.scrollTop;
  const contentBottom = last.getBoundingClientRect().bottom - viewportTop + list.scrollTop;
  const minTop = Math.max(0, contentBottom - list.clientHeight);
  const boundary = rows
    .map(offsetTop)
    .filter((top) => top >= minTop - 0.5)
    .sort((left, right) => left - right)[0] ?? 0;
  const pad = Math.max(0, Math.ceil(boundary - contentBottom + list.clientHeight));
  if (pad !== (Number.parseFloat(list.style.paddingBottom) || 0)) {
    list.style.paddingBottom = pad > 0 ? `${pad}px` : '';
  }
  list.scrollTo({ top: boundary, behavior: smooth ? 'smooth' : 'auto' });
}

/**
 * Case-page layout: a bounded timeline (duration gutter, dot and rule) that follows the newest
 * exchange during replay, then one gate card holding the proposal and the human decision.
 */
function CaseInvestigationView({
  trace,
  rows,
  visibleCount,
  recommendation,
  decision,
  decisionAtLabel,
  decisionReason,
  canDecide,
  playing,
  cursorLabel,
  onPlay,
  onDecisionReasonChange,
  onDecision,
  showRecommendation = false,
  controlPlanLabel,
  runLabel,
  actionGate,
  approverLabel,
  actionApproval,
  run,
  runStatus,
  canRun,
  onRun,
  onTogglePlayback,
  stepClocks,
  endClock,
  showMoney = true,
}: InvestigationReplayViewProps) {
  // The gate closes on this page's approval or on the session's recorded one (D19).
  const approvedAt = decision?.kind === 'approved'
    ? { at: decision.at, by: decision.approver }
    : actionApproval;
  const approved = approvedAt !== undefined && (decision === null || decision.kind === 'approved');
  const listRef = useRef<HTMLOListElement | null>(null);
  const recordRef = useRef<HTMLParagraphElement | null>(null);
  const decidedRef = useRef(decision !== null);
  const followRef = useRef(false);
  const [listScrolled, setListScrolled] = useState(false);
  const visibleRows = rows.slice(0, visibleCount);
  const complete = visibleCount >= rows.length;
  const estimate = recommendation ? formatAgentEstimate(recommendation.estimated_value) : null;
  const showGate = Boolean(complete && showRecommendation && recommendation && estimate);

  // Follow the newest exchange once a replay has started; a page opened at rest
  // reads from the top. The list lands on a row boundary so a finished run never
  // shows a half-clipped row under the controls (K15 round 2).
  useEffect(() => {
    if (playing) followRef.current = true;
    const list = listRef.current;
    if (!list || !followRef.current) return;
    scrollListToRowBoundary(list, !prefersReducedMotion());
  }, [playing, visibleCount, complete, showGate]);

  // A decision made here unmounts the buttons; hand focus to the record instead of the body.
  useEffect(() => {
    if (decision && !decidedRef.current) recordRef.current?.focus({ preventScroll: true });
    decidedRef.current = decision !== null;
  }, [decision]);

  return (
    <section
      className="flex h-full min-h-0 flex-col gap-2.5"
      aria-label="Recorded agent investigation"
      data-trace-model={trace.model}
      data-trace-recorded-at={trace.recorded_at}
      data-investigation-run={run?.id ?? 'none'}
    >
      {WorkflowStrip({ run, status: runStatus })}
      <div className="min-w-0">
        {RunControls({
          run,
          playing,
          complete,
          canRun: canRun ?? false,
          onRun,
          onTogglePlayback: onTogglePlayback ?? onPlay,
        })}
        {runLabel ? <p className="mt-0.5 truncate text-[10px] text-muted" data-investigation-run-label>{runLabel}</p> : null}
      </div>

      {visibleRows.length > 0 ? (
        <div className="relative min-h-0">
          {listScrolled ? (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 z-10 h-3 bg-gradient-to-b from-[var(--surface-2)] to-transparent"
              data-agent-tool-fade
            />
          ) : null}
          <ol
            ref={listRef}
            onScroll={(event) => setListScrolled(event.currentTarget.scrollTop > 2)}
            className={cn(
              'min-h-0 overflow-y-auto pr-1',
              showGate ? 'max-h-[6.75rem]' : 'max-h-[20rem]',
            )}
            aria-label="Agent tool trace"
            data-agent-tool-scroll="bounded"
          >
            {visibleRows.map((row, index) => {
              const newest = index === visibleRows.length - 1;
              const last = newest && !complete;
              return (
                <li
                  key={`${row.tool}-${index}`}
                  data-agent-tool={row.tool}
                  className="grid grid-cols-[2.4rem_0.75rem_minmax(0,1fr)] gap-x-1.5 pb-2.5 last:pb-0"
                >
                  <span className="pt-px text-right text-[10px] tabular-nums text-muted" data-agent-step-clock={stepClocks?.[index] ?? ''}>
                    {stepClocks?.[index] ?? formatAgentDuration(row.elapsedMs)}
                  </span>
                  <span aria-hidden="true" className="relative flex justify-center">
                    {!last ? <span className="absolute -bottom-2.5 top-3 w-px bg-[var(--hairline-strong)]" /> : null}
                    <span className={cn('relative mt-1 size-2 rounded-full', newest && playing ? 'bg-[var(--color-accent-500)]' : 'bg-[var(--ink-tertiary)]')} />
                  </span>
                  <div className="min-w-0">
                    <details className="group">
                      <summary className="flex cursor-pointer list-none items-baseline gap-1.5 [&::-webkit-details-marker]:hidden">
                        <span className="shrink-0 rounded bg-[var(--surface-3)] px-1 py-px text-[10px] font-medium text-ink-primary">{agentToolLabel(row.tool)}</span>
                        {row.argumentLabel ? <span className="min-w-0 text-[10px] leading-snug text-muted">{row.argumentLabel}</span> : null}
                        <span className="ml-auto shrink-0 text-[10px] text-muted group-open:text-ink-secondary">Reasoning</span>
                      </summary>
                      <p className="mt-1 text-[10px] leading-snug text-muted">{row.reasoning}</p>
                    </details>
                    <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-ink-primary">{row.resultExcerpt}</p>
                  </div>
                </li>
              );
            })}
            {complete ? (
              <li className="grid grid-cols-[2.4rem_0.75rem_minmax(0,1fr)] gap-x-1.5" data-agent-trace-end>
                <span className="pt-px text-right text-[10px] tabular-nums text-muted" data-agent-end-clock={endClock ?? ''}>{endClock}</span>
                <span aria-hidden="true" className="relative flex justify-center">
                  <span className="absolute top-0 h-1.5 w-px bg-[var(--hairline-strong)]" />
                  <span className="relative mt-1.5 size-2 rounded-full bg-[var(--state-online)]" />
                </span>
                <span><Pill tone="success" className="px-1.5 text-[10px]">Recommendation ready</Pill></span>
              </li>
            ) : null}
          </ol>
        </div>
      ) : null}

      {showGate && recommendation && estimate ? (
        <div
          className={cn(
            'flex flex-col gap-2 rounded-[14px] border px-3.5 py-3 transition-colors duration-300',
            approved ? GATE_APPROVED_SURFACE : 'border-[var(--hairline-strong)] bg-[var(--surface-2)]',
          )}
          data-approval-gate={approved ? 'approved' : decision ? decision.kind : 'draft'}
        >
          <span className={cn(gatePillClass(approved ? 'approved' : 'draft'), 'self-start')} data-action-gate-pill>
            {approved && approvedAt
              ? approvedPillText(approvedAt.at, approvedAt.by)
              : decision
                ? `${agentDecisionLabel(decision.kind)} · ${decision.at.slice(11, 16)} · ${gateSeatLabel(decision.approver)}`
                : 'Draft — not live until approved'}
          </span>
          <p className="text-[13px] font-semibold leading-snug text-ink-primary" data-agent-action-lead>
            {actionGate?.proposition ?? recommendation.control_plan_action}
          </p>
          {actionGate ? (
            <div className="-mt-0.5 space-y-0.5 text-[11px] leading-snug text-muted tabular-nums">
              <p data-action-gate-figures>{actionGate.figureLine}</p>
              {actionGate.recoveryLine ? <p data-action-gate-recovery>{actionGate.recoveryLine}</p> : null}
            </div>
          ) : null}
          <details className="text-[10px] leading-snug text-ink-secondary" data-agent-recommendation>
            <summary className="cursor-pointer font-medium text-ink-primary">
              Evidence · {recommendation.evidence.length} facts and what to check
            </summary>
            <div className="mt-1.5 space-y-1.5">
              <p>
                <span className="text-muted">Most likely cause</span>
                {' · '}
                <span className="font-medium text-ink-primary" data-cause-id={recommendation.cause_id}>{recommendation.cause_label}</span>
                {' · '}{recommendation.confidence_pct}% · cause unconfirmed
              </p>
              <p><span className="text-muted">TARP · Tier {recommendation.tarp_tier}</span>{' · '}{recommendation.tarp_instruction}</p>
              {showMoney ? (
                <p className="tabular-nums">
                  <span className="font-medium text-ink-primary">Estimated EBITDA impact · {estimate.impact}</span>
                  {' · '}
                  <span className="text-muted"><span className="whitespace-nowrap">{RUN_ESTIMATE_NAME}</span>{RUN_ESTIMATE_REST}</span>
                  {' · '}
                  <span className="text-muted" data-agent-estimate-recovery>{estimate.recovery} · {estimate.shortfall}</span>
                </p>
              ) : (
                <p className="tabular-nums" data-agent-estimate-basis>{estimateBasisLine(recommendation.estimated_value)}</p>
              )}
              <ul className="space-y-1">
                {recommendation.evidence.map((fact, index) => <li key={index}>{fact}</li>)}
              </ul>
              <p><span className="text-muted">What to check</span>{' · '}{recommendation.human_check}</p>
              <p><span className="text-muted">Control Plan · {controlPlanLabel ?? recommendation.control_plan_row_id}</span>{' · '}{recommendation.control_plan_action}</p>
            </div>
          </details>
          {approved ? (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" disabled className={cn(GATE_PRIMARY_BUTTON, 'h-8 disabled:opacity-100')}>Approved</button>
              {decision ? (
                <p ref={recordRef} tabIndex={-1} className="min-w-0 flex-1 rounded text-[11px] leading-snug text-ink-secondary outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent-500)]" data-approval-record>
                  {decision.reason}
                </p>
              ) : <span className="sr-only" data-approval-record>{approvedAt ? approvedPillText(approvedAt.at, approvedAt.by) : ''}</span>}
            </div>
          ) : decision ? (
            <DecisionRecord decision={decision} decisionAtLabel={decisionAtLabel} recordRef={recordRef} className="rounded text-xs leading-snug text-ink-primary outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent-500)]" />
          ) : <ActionGateControls reason={decisionReason} onReason={onDecisionReasonChange} onDecision={onDecision} seat={approverLabel ?? 'Approver'} canDecide={canDecide} cursorLabel={cursorLabel} />}
        </div>
      ) : null}
    </section>
  );
}
